import React, { memo, useMemo, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// ✅ Move static styles outside component to prevent recreation
const CONTAINER_STYLES = {
  margin: '24px 0',
  position: 'relative',
  borderRadius: '20px',
  overflow: 'hidden',
  cursor: 'pointer',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
  minHeight: '160px',
  willChange: 'transform'
};

const BACKGROUND_PATTERN_STYLES = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: `
    radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
  `,
  zIndex: 1,
  pointerEvents: 'none'
};

const CONTENT_WRAPPER_STYLES = {
  position: 'relative',
  zIndex: 2,
  padding: '24px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
};

const ICON_CONTAINER_STYLES = {
  width: '48px',
  height: '48px',
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  border: '1px solid rgba(255, 255, 255, 0.3)'
};

const ARROW_BUTTON_STYLES = {
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  fontSize: '16px',
  color: 'white'
};

const BADGE_BASE_STYLES = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: 'rgba(255, 255, 255, 0.15)',
  padding: '6px 12px',
  borderRadius: '20px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)'
};

const SHIMMER_STYLES = {
  position: 'absolute',
  top: 0,
  left: '-100%',
  width: '100%',
  height: '100%',
  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
  animation: 'shimmer 3s infinite',
  zIndex: 3,
  pointerEvents: 'none'
};

// ✅ Static animation variants (created once)
const MOTION_VARIANTS = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  hover: { 
    scale: 1.02,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)',
    y: -4
  },
  tap: { scale: 0.98 }
};

const MOTION_TRANSITION = {
  duration: 0.5,
  delay: 0.3
};

// ✅ Memoized Badge Component
const Badge = memo(({ icon, label }) => (
  <div style={BADGE_BASE_STYLES}>
    <span style={{ fontSize: '12px' }}>{icon}</span>
    <span style={{ 
      fontSize: '12px', 
      color: 'white', 
      fontWeight: '600' 
    }}>
      {label}
    </span>
  </div>
), (prevProps, nextProps) => {
  return prevProps.icon === nextProps.icon && prevProps.label === nextProps.label;
});

Badge.displayName = 'Badge';

// ✅ Memoized Header Section
const HeaderSection = memo(() => {
  const { t } = useTranslation();
  return (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px'
  }}>
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    }}>
      <div style={ICON_CONTAINER_STYLES}>
        📅
      </div>
      <div>
        <h3 style={{
          fontSize: '20px',
          fontWeight: '700',
          color: 'white',
          margin: 0,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
          {t('events.allEventsListTitle')}
        </h3>
        <p style={{
          fontSize: '14px',
          color: 'rgba(255, 255, 255, 0.9)',
          margin: '4px 0 0 0',
          fontWeight: '500'
        }}>
          {t('events.browseUpcoming')}
        </p>
      </div>
    </div>

    <div style={ARROW_BUTTON_STYLES}>
      →
    </div>
  </div>
  );
});

HeaderSection.displayName = 'HeaderSection';

// ✅ Memoized Bottom Section
const BottomSection = memo(() => {
  const { t } = useTranslation();
  return (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <div style={{
      display: 'flex',
      gap: '16px',
      alignItems: 'center'
    }}>
      <Badge icon="🎉" label={t('events.liveEventsBadge')} />
      <Badge icon="📍" label={t('events.venueBasedBadge')} />
    </div>

    <div style={{
      fontSize: '12px',
      color: 'rgba(255, 255, 255, 0.8)',
      fontWeight: '500',
      textAlign: 'right'
    }}>
      <div>{t('events.discoverAmazingPrefix')}</div>
      <div style={{ fontWeight: '700' }}>{t('events.eventsNearYouSuffix')}</div>
    </div>
  </div>
  );
});

BottomSection.displayName = 'BottomSection';

// ✅ Main Banner Component with Optimization
const AllEventsBanner = ({ onClick }) => {
  // ✅ Memoize onClick handler
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  // ✅ Memoize hover variants
  const hoverVariant = useMemo(() => MOTION_VARIANTS.hover, []);
  const tapVariant = useMemo(() => MOTION_VARIANTS.tap, []);

  return (
    <>
      <motion.div
        initial={MOTION_VARIANTS.initial}
        animate={MOTION_VARIANTS.animate}
        transition={MOTION_TRANSITION}
        whileHover={hoverVariant}
        whileTap={tapVariant}
        onClick={handleClick}
        style={CONTAINER_STYLES}
      >
        {/* Background Pattern - Static layer */}
        <div style={BACKGROUND_PATTERN_STYLES} />

        {/* Content */}
        <div style={CONTENT_WRAPPER_STYLES}>
          {/* Top Section */}
          <HeaderSection />

          {/* Bottom Section */}
          <BottomSection />
        </div>

        {/* Shimmer Effect */}
        <div style={SHIMMER_STYLES} />
      </motion.div>

      {/* ✅ Keyframe animations moved to global style */}
      <style jsx>{`
        @keyframes shimmer {
          0% { 
            transform: translateX(0);
          }
          100% { 
            transform: translateX(200%);
          }
        }
      `}</style>
    </>
  );
};

// ✅ Memoize main component with custom comparison
export default memo(AllEventsBanner, (prevProps, nextProps) => {
  return prevProps.onClick === nextProps.onClick;
});
